import * as React from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ComboboxOption {
  label: string
  value: string
  searchKeywords?: string[]
}

interface ComboboxCreatableProps {
  options: ComboboxOption[]
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  noOptionsMessage?: string
  /** Regex pattern to filter typed input characters (e.g. /[^0-9]/g for integers only) */
  inputFilter?: RegExp
  /** When false, only values present in options can be selected (search-only, no free text). Defaults to true. */
  allowCustomValue?: boolean
}

const ComboboxCreatable: React.FC<ComboboxCreatableProps> = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Select or type...',
  disabled = false,
  className,
  inputFilter,
  allowCustomValue = true,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  // Track whether a dropdown item is being clicked so onBlur doesn't steal the event
  const isSelectingRef = React.useRef(false)

  const selectedLabel = React.useMemo(() => {
    if (!value) return ''
    const opt = options.find((o) => o.value === value)
    return opt?.label || value
  }, [value, options])

  React.useEffect(() => {
    if (!isOpen) {
      setInputValue('')
      setHighlightedIndex(-1)
    }
  }, [isOpen])

  // Click-outside: commit typed value and close
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        const typed = inputValue.trim()
        if (typed && allowCustomValue) {
          onChange(typed)
        }
        setIsOpen(false)
        setInputValue('')
        onBlur?.()
      }
    }
    // Use click (not mousedown) so dropdown item onMouseDown fires first
    document.addEventListener('click', handleClickOutside, true)
    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [inputValue, onChange, onBlur, allowCustomValue])

  const filteredOptions = React.useMemo(() => {
    if (!inputValue.trim()) return options
    const lower = inputValue.toLowerCase()
    return options.filter((o) =>
      o.label.toLowerCase().includes(lower) ||
      o.searchKeywords?.some((kw) => kw.toLowerCase().includes(lower))
    )
  }, [options, inputValue])

  // Only show dropdown when there are matching options (or always, in search-only mode)
  const hasMatchingOptions = filteredOptions.length > 0
  const showDropdown = isOpen && (hasMatchingOptions || !allowCustomValue)

  const handleSelect = (val: string) => {
    onChange(val)
    setIsOpen(false)
    setInputValue('')
    isSelectingRef.current = false
    onBlur?.()
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    setIsOpen(false)
    onBlur?.()
  }

  const commitAndClose = () => {
    const typed = inputValue.trim()
    if (typed && allowCustomValue) {
      onChange(typed)
    }
    setIsOpen(false)
    setInputValue('')
    onBlur?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!hasMatchingOptions) {
      // No options — behave like a plain text input (only when custom values allowed)
      if (e.key === 'Enter') {
        e.preventDefault()
        commitAndClose()
      } else if (e.key === 'Escape') {
        setIsOpen(false)
        setInputValue('')
      } else if (e.key === 'Tab') {
        commitAndClose()
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex].value)
      } else if (inputValue.trim() && allowCustomValue) {
        commitAndClose()
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setInputValue('')
    } else if (e.key === 'Tab') {
      commitAndClose()
    }
  }

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-combobox-item]')
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger / Input */}
      {isOpen ? (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            const newVal = inputFilter ? e.target.value.replace(inputFilter, '') : e.target.value
            setInputValue(newVal)
            setHighlightedIndex(-1)
            // In free-text mode (no matching options), update the value live
            // so the form always has the latest typed text
            const lower = newVal.toLowerCase()
            const hasMatches = options.some((o) =>
              o.label.toLowerCase().includes(lower) ||
              o.searchKeywords?.some((kw) => kw.toLowerCase().includes(lower))
            )
            if (!hasMatches && newVal.trim() && allowCustomValue) {
              onChange(newVal.trim())
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Delay to let dropdown item onMouseDown set the flag
            setTimeout(() => {
              if (!isSelectingRef.current) {
                commitAndClose()
              }
            }, 150)
          }}
          placeholder={selectedLabel || placeholder}
          disabled={disabled}
          autoFocus
          className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 pr-16 text-[0.8rem] placeholder:text-muted-foreground focus:outline-none focus:border-black disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[#f0f0f0] transition-colors duration-200"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen(true)
              setTimeout(() => inputRef.current?.focus(), 0)
            }
          }}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-[0.8rem] text-left transition-colors duration-200',
            'focus:outline-none focus:border-black',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[#f0f0f0]',
            !value && 'text-muted-foreground'
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="rounded-full p-0.5 hover:bg-slate-100"
              >
                <X className="h-3 w-3 text-slate-400" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </button>
      )}

      {/* Dropdown — only shown when there are matching options */}
      {showDropdown && (
        <div
          className="absolute z-[450] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden"
          onMouseDown={(e) => {
            // Prevent input blur when clicking inside dropdown
            e.preventDefault()
          }}
        >
          <div ref={listRef} className="max-h-[200px] overflow-y-auto py-1">
            {!hasMatchingOptions && !allowCustomValue && (
              <div className="px-3 py-2 text-[0.8rem] text-muted-foreground">
                No results
              </div>
            )}
            {filteredOptions.map((option, idx) => {
              const isSelected = option.value === value
              const isHighlighted = idx === highlightedIndex
              return (
                <div
                  key={option.value}
                  data-combobox-item
                  onMouseDown={() => {
                    isSelectingRef.current = true
                  }}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-[0.8rem] cursor-pointer',
                    isHighlighted && 'bg-slate-100',
                    isSelected && 'font-medium',
                    !isHighlighted && 'hover:bg-slate-50'
                  )}
                >
                  <span className="flex-1 truncate">{option.label}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export { ComboboxCreatable }
export type { ComboboxOption, ComboboxCreatableProps }
